import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Check,
} from "lucide-react";

// ---------- Account Tab (admin self-edit) ----------
export default function AccountTab() {
    const { user } = useAuth();
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", current_password: "", new_password: "" });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        setForm((f) => ({ ...f, name: user?.name || "", email: user?.email || "" }));
    }, [user]);

    const save = async () => {
        setMsg(null);
        setSaving(true);
        const payload = {};
        if (form.name && form.name !== user?.name) payload.name = form.name;
        if (form.email && form.email !== user?.email) payload.email = form.email;
        if (form.new_password) {
            payload.current_password = form.current_password;
            payload.new_password = form.new_password;
        }
        if (Object.keys(payload).length === 0) {
            setMsg({ ok: false, text: "No fields changed" });
            setSaving(false);
            return;
        }
        try {
            await api.patch("/auth/me", payload);
            setMsg({ ok: true, text: "✓ Saved. Reload to see changes in header." });
            setForm({ ...form, current_password: "", new_password: "" });
        } catch (e) {
            setMsg({ ok: false, text: e.response?.data?.detail || "Save failed" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-account-tab" className="max-w-xl space-y-4">
            <div className="label-kicker text-slate-500">Your account</div>
            <p className="text-sm text-slate-600">
                Update the admin display name, sign-in email, or password.
            </p>

            <div className="space-y-3 border border-slate-200 p-4 bg-slate-50">
                <div>
                    <label className="label-kicker mb-1 block">Display name</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="account-name-input" />
                </div>
                <div>
                    <label className="label-kicker mb-1 block">Email</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="account-email-input" />
                </div>

                <div className="border-t border-slate-200 pt-3">
                    <div className="label-kicker text-slate-500 mb-2">Change password (optional)</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        <div>
                            <label className="label-kicker mb-1 block">Current password</label>
                            <input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="input-field" data-testid="account-current-password-input" />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">New password (min 6)</label>
                            <input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="input-field" data-testid="account-new-password-input" />
                        </div>
                    </div>
                </div>

                {msg && (
                    <div className={`text-xs font-mono p-2 border ${msg.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="account-save-message">
                        {msg.text}
                    </div>
                )}

                <button onClick={save} disabled={saving} className="btn-primary" data-testid="account-save-button">
                    <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );
}

